import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlaying = () => {
      setVideoPlaying(true);
      video.playbackRate = 0.4;
    };

    video.addEventListener("playing", handlePlaying);

    // Mobile browsers may not autoplay; explicitly attempt play
    const tryPlay = () => {
      video.play().catch(() => {
        // If blocked, try once on first user interaction
        const onInteraction = () => {
          video.play().catch(() => setVideoFailed(true));
          document.removeEventListener("touchstart", onInteraction);
          document.removeEventListener("click", onInteraction);
        };
        document.addEventListener("touchstart", onInteraction, { once: true });
        document.addEventListener("click", onInteraction, { once: true });
      });
    };

    // Wait for enough data before playing
    if (video.readyState >= 3) {
      tryPlay();
    } else {
      video.addEventListener("canplay", tryPlay, { once: true });
    }

    return () => {
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("canplay", tryPlay);
    };
  }, []);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.8], [0.3, 0.7]);

  return (
    <section ref={sectionRef} className="relative h-[90vh] min-h-[600px] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: videoY }}>
        {videoFailed && (
          <div className="w-full h-[120%] bg-foreground" />
        )}
        {!videoFailed && (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-[120%] object-cover"
            onError={() => setVideoFailed(true)}
          >
            <source src="/videos/nott-hero.mov" type="video/mp4" />
            <source src="/videos/nott-hero.mov" type="video/quicktime" />
          </video>
        )}
      </motion.div>
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-foreground/40 via-foreground/20 to-foreground/70"
        style={{ opacity: overlayOpacity }}
      />

      <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="font-body text-xs tracking-[0.35em] uppercase text-primary-foreground/70 mb-4"
        >
          Hand-crocheted in Amsterdam
        </motion.p>

        <h1
          className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-primary-foreground leading-[0.95] mb-6"
        >
          Clouds, Stripes
          <br />
          <span className="italic font-normal">& Organza</span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="font-body text-base md:text-lg text-primary-foreground/85 max-w-md mb-8"
        >
          Texture meets colour. Every piece is one of a kind, made by hand, slowly and carefully.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Button variant="hero" size="lg" asChild>
            <Link to="/shop?collection=spring-2026">Shop New Arrivals</Link>
          </Button>
          <Button variant="heroOutline" size="lg" asChild>
            <Link to="/shop">Shop All</Link>
          </Button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-[1px] h-8 bg-primary-foreground/40"
        />
      </motion.div>
    </section>
  );
};

export default HeroSection;
